import type { DataChartStyleProps } from './datachartStyleRegistry'
import { useEnergyDeviceSnapshot } from './useEnergyDeviceSnapshot'
import './EnergyCard.css'

type NumericPhase = {
  id: 'r' | 's' | 't'
  label: string
  color: string
  power: number | null
  voltage: number | null
  current: number | null
  power_factor: number | null
}

function formatInt(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '--'
  return value.toLocaleString('en-US', {
    maximumFractionDigits: 0,
  })
}

function formatFixed(value: number | null, digits: number): string {
  if (value === null || !Number.isFinite(value)) return '--'
  return value.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

function NumericPhaseBlock({ phase }: { phase: NumericPhase }) {
  return (
    <div className="energy-card-numeric__phase">
      <div className="energy-card-numeric__phase-label">
        <span className="energy-card-numeric__phase-bar" style={{ background: phase.color }} />
        <span>{phase.label}</span>
      </div>
      <div className="energy-card-numeric__phase-power">
        {formatInt(phase.power)}
        <small>W</small>
      </div>
      <div className="energy-card-numeric__phase-rule" style={{ background: `${phase.color}4D` }} />
      <div className="energy-card-numeric__metric-row">
        <span>VOLT</span>
        <strong>{formatFixed(phase.voltage, 1)}V</strong>
      </div>
      <div className="energy-card-numeric__metric-row">
        <span>AMP</span>
        <strong>{formatFixed(phase.current, 1)}A</strong>
      </div>
      <div className="energy-card-numeric__metric-row">
        <span>PF</span>
        <strong>{formatFixed(phase.power_factor, 2)}</strong>
      </div>
    </div>
  )
}

function formatTime(timestamp: number): string {
  if (!Number.isFinite(timestamp)) return '--:--:--'
  return new Date(timestamp).toLocaleTimeString('en-US', { hour12: false })
}

export function EnergyCardStyle01(props: DataChartStyleProps) {
  const { snapshot } = useEnergyDeviceSnapshot(props.deviceSN, props.credentials)
  const numericPhases: NumericPhase[] = [
    {
      id: 'r',
      label: 'R-PHASE',
      color: '#ef4444',
      power: snapshot?.phases.r.power ?? null,
      voltage: snapshot?.phases.r.voltage ?? null,
      current: snapshot?.phases.r.current ?? null,
      power_factor: snapshot?.phases.r.powerFactor ?? null,
    },
    {
      id: 's',
      label: 'S-PHASE',
      color: '#f59e0b',
      power: snapshot?.phases.s.power ?? null,
      voltage: snapshot?.phases.s.voltage ?? null,
      current: snapshot?.phases.s.current ?? null,
      power_factor: snapshot?.phases.s.powerFactor ?? null,
    },
    {
      id: 't',
      label: 'T-PHASE',
      color: '#3b82f6',
      power: snapshot?.phases.t.power ?? null,
      voltage: snapshot?.phases.t.voltage ?? null,
      current: snapshot?.phases.t.current ?? null,
      power_factor: snapshot?.phases.t.powerFactor ?? null,
    },
  ]
  const combinedPower = snapshot?.totalPower ?? null

  return (
    <div className="energy-card energy-card--numeric" data-css-slot="cardFrame" data-css-slot-label="Card Frame">
      <div className="energy-card-numeric__head">
        <div className="energy-card-numeric__head-title">PowerCard - Pure Numeric</div>
        <span className="material-symbols-rounded" aria-hidden>
          info
        </span>
      </div>

      <div className="energy-card-numeric__phase-grid">
        {numericPhases.map((phase) => (
          <NumericPhaseBlock key={phase.id} phase={phase} />
        ))}
      </div>

      <div className="energy-card-numeric__footer">
        <div>
          <div className="energy-card-numeric__footer-label">Combined Total Power</div>
          <div className="energy-card-numeric__footer-value">{formatInt(combinedPower)}W</div>
        </div>
        <div className="energy-card-numeric__footer-updated">
          <span>Last Update</span>
          <strong>{formatTime(snapshot?.updatedAt ?? Number.NaN)}</strong>
        </div>
      </div>
    </div>
  )
}
